import { formatISODatetimeForAnalytics } from 'sprout-api-client/src/dates';
import parseISO from 'date-fns/parseISO';
import add from 'date-fns/add';
import { makeSlices, reportErrorReason } from './general-helpers';
var GetDataContext = /** @class */ (function () {
    function GetDataContext(connector, table, done, rowsFormatter, fields, metrics) {
        this.connector = connector;
        this.table = table;
        this.done = done;
        this.rowsFormatter = rowsFormatter;
        this.fields = fields;
        this.metrics = metrics;
        this.numPosts = 0;
    }
    GetDataContext.prototype.getNumPosts = function () {
        return this.numPosts;
    };
    GetDataContext.prototype.addNumPosts = function (num) {
        this.numPosts += num;
    };
    return GetDataContext;
}());
var PostsRequestContext = /** @class */ (function () {
    function PostsRequestContext(getDataContext, customerProfileIds, start, end, requestProgressFormatter) {
        this.getDataContext = getDataContext;
        this.customerProfileIds = customerProfileIds;
        // copy the value of start, since it's mutable
        this.start = start;
        // add 1 day to account for posts in a "future" timezone as we really want all latest posts regardless of timezone
        this.end = add(end, { days: 1 });
        this._requestProgressFormatter = requestProgressFormatter;
    }
    Object.defineProperty(PostsRequestContext.prototype, "connector", {
        get: function () {
            return this.getDataContext.connector;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PostsRequestContext.prototype, "table", {
        get: function () {
            return this.getDataContext.table;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PostsRequestContext.prototype, "rowsFormatter", {
        get: function () {
            return this.getDataContext.rowsFormatter;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PostsRequestContext.prototype, "fields", {
        get: function () {
            return this.getDataContext.fields;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PostsRequestContext.prototype, "metrics", {
        get: function () {
            return this.getDataContext.metrics;
        },
        enumerable: false,
        configurable: true
    });
    PostsRequestContext.prototype.getNumPosts = function () {
        return this.getDataContext.getNumPosts();
    };
    PostsRequestContext.prototype.addNumPosts = function (num) {
        this.getDataContext.addNumPosts(num);
    };
    Object.defineProperty(PostsRequestContext.prototype, "requestProgressMessageFormatter", {
        get: function () {
            return this._requestProgressFormatter;
        },
        enumerable: false,
        configurable: true
    });
    return PostsRequestContext;
}());
export var asyncGetPosts = function (_a) {
    var connector = _a.connector, table = _a.table, done = _a.done, body = _a.body, rowsFormatter = _a.rowsFormatter, requestProgressMessageFormatter = _a.requestProgressMessageFormatter;
    var getDataContext = new GetDataContext(connector, table, done, rowsFormatter, body.fields, body.metrics);
    connector.getCustomerProfileIdsSupportedByAnalytics()
        .then(function (customerProfileIds) {
        var promises = makeSlices(customerProfileIds, 100).map(function (idSlice) {
            // We're deliberately _not_ slicing dates here like we do in Profiles!!!
            // It turns out we don't need to. The Posts endpoints to do not limit the date range.
            var requestContext = new PostsRequestContext(getDataContext, idSlice, body.start, body.end, requestProgressMessageFormatter);
            return getPostsWithContext(requestContext);
        });
        return Promise.all(promises);
    })
        .then(function () {
        getDataContext.done();
    })
        .catch(function (reason) {
        reportErrorReason(reason, getDataContext.connector.getTableau());
    })
        .finally(function () {
        getDataContext.connector.getTableau().reportProgress('');
    });
};
var getPostsWithContext = function (context) {
    return new Promise(function (resolve, reject) {
        setTimeout(function () {
            var body = {
                filters: [
                    "customer_profile_id.eq(".concat(context.customerProfileIds.join(','), ")"),
                    "created_time.in(".concat(formatISODatetimeForAnalytics(context.start), "..").concat(formatISODatetimeForAnalytics(context.end), ")"),
                ],
                sort: ['created_time:asc'],
                fields: context.fields,
                metrics: context.metrics,
                limit: 1000,
                timezone: 'UTC',
            };
            if (context.excludedPostGuids) {
                body.filters.push("guid.neq(".concat(context.excludedPostGuids.join(','), ")"));
            }
            return context.connector
                .getApiClient()
                .fetchAnalyticsPosts(body)
                .then(function (r) { return r.json(); })
                .then(makeProcessResponseFn(context))
                .then(function (result) {
                if (result.lastCreatedTime) {
                    context.start = result.lastCreatedTime;
                    context.excludedPostGuids = result.seenPostGuids;
                    context.addNumPosts(result.seenPostGuids.length);
                    context.connector.getTableau().reportProgress(context.requestProgressMessageFormatter(context.getNumPosts()));
                    resolve(getPostsWithContext(context));
                }
                else {
                    resolve();
                }
            })
                .catch(reject);
        }, 0);
    });
};
/**
 * Given a {@link RequestContext} containing general Sprout Public API response ... return a function that can process
 * a Sprout Public API response and return a {@link ResponseProcessResult}. The ResponseProcessResult will have a full
 * list of all Post Ids encountered so far and the date of the last Post processed. The Tableau table data is updated
 * as well during this processing.
 *
 * Note: We're avoiding using the `page` in the API response because it's hard on the Elasticsearch backend and thus
 * slow. Instead, we'll look at the response and, using a limited response size and details about the last Post in each
 * response, construct the next request.
 * @param context a {@link RequestContext}
 * @returns a function that takes a {@link ApiResponse} and a {@link RequestContext} and produces a {@link ResponseProcessResult}.
 */
var makeProcessResponseFn = function (context) {
    return function (response) {
        response.data;
        var lastPost = response.data[response.data.length - 1];
        var responseProcessResult = {
            seenPostGuids: new Array(),
            lastCreatedTime: lastPost ? parseISO(lastPost.created_time) : undefined,
        };
        response.data.forEach(function (post) {
            responseProcessResult.seenPostGuids.push(post.guid);
        });
        context.table.appendRows(context.rowsFormatter(response));
        return responseProcessResult;
    };
};
